import labelsDataRepository from "./labels.mjs";

if ( typeof window.glob !== 'object' ) {

	// -----------------------------------------------------------------------------------------------------------------
	/** { Object } */
	const glob = {
		/** @private */
		prop: { registered: {}, status: {}, data: {} }
	};
	glob.prop.keyApplicationName = 'domino-items';
	glob.prop.isDeploy = ( process.env.NODE_ENV === "production" );
	glob.prop.appVersion = window.document.querySelector("head > meta[name='app-version']").getAttribute("content") || '';
	glob.prop.uri = { server: "" };
	glob.prop.uri.onlinebackend = '/backend/';
	glob.prop.uri.offlinebackend = 'http://localhost:3077/backend/';
	glob.prop.uri.api = glob.prop.uri[ glob.prop.isDeploy ? 'onlinebackend' : 'offlinebackend'];
	glob.prop.uri.localpath = window.location.pathname;
	glob.prop.uri.web = glob.prop.uri.server + glob.prop.uri.localpath;
	glob.prop.uri.formdef = glob.prop.uri.api+'getForm?_';
	glob.prop.uri.saveform = glob.prop.uri.api + "saveItem";
	glob.prop.uri.getform = glob.prop.uri.api + "getItem";
	glob.prop.appRootNode = document.getElementById("appRootNode");
	glob.prop.homePage = 'MainMenu';
	glob.prop.labelsDB = labelsDataRepository();
	// -----------------------------------------------------------------------------------------------------------------

	/**
	 * @function
	 * @param { string } [prefix]
	 * @return { string }
	 * @description Generates a UUID prefixed with some item type - defaults to "item"
	 */
	glob.generateUid = ( prefix = "item") => {
		return prefix + "-" + ("10000000-1048-4000-8000-1400800110084").replace(/[018]/g, (c) =>
			(c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
		);
	};

	/**
	 * @function
	 * @param {*} x
	 * @return {undefined|*}
	 */
	glob.clone = (x) => {
		if (typeof x === "undefined") return undefined;
		if ( x && typeof x === 'object' ) try {
			return window.structuredClone ? window.structuredClone(x) : JSON.parse(JSON.stringify(x));
		} catch (e) { }
		return x;
	};

	/**
	 * @function
	 * @return {string} The app version declared in package.json
	 * @description Returns app version declared in package.json
	 */
	glob.getAppVersion = () => process.env.REACT_APP_VERSION;

	/**
	 * @private
	 * @function
	 * @return {void}
	 * @description Only in production: checks if app needs to be updated, if yes reloads.
	 */
	glob.checkForUpdate = () => {
		if ( ! glob.prop.isDeploy ) return;
		const curVersion = glob.getAppVersion();
		if ( ! curVersion ) return;
		let latestVersion;
		fetch(
			`${process.env.PUBLIC_URL}/index.html`,
			{
				method: 'GET',
				cache: 'no-cache',
				credentials: 'include',
				referrerPolicy: 'no-referrer',
				headers: { pragma: 'no-cache', 'cache-control': 'no-cache' }
			}
		)
			.then(res => res.text())
			.then(htmlString => {
				let doc = new DOMParser().parseFromString(htmlString, "text/html");
				latestVersion = doc
					.querySelector("head > meta[name='app-version']")
					.getAttribute("content");
				if (latestVersion !== curVersion) window.location.reload(true);
			})
			.catch(e=>{
				console.error('Cannot contact server');
			})
	}

	if (glob.prop.isDeploy) {
		window.document.addEventListener(
			"visibilitychange",
			evt => {
				if ( evt.target.visibilityState === "visible" ) setTimeout(glob.checkForUpdate);
			}
		);
	}

	/**
	 * @function
	 * @param { string|[string]} contexts The name(s) ot the status change variable(s)
	 * @param { function } trigger
	 * @param { string } [uid]
	 * @return { (function(): void)|* }
	 * @description Register a function handle on a status change
	 */
	glob.register = ( contexts, trigger, uid ) => {
		if ( typeof contexts === 'string' ) contexts = [contexts];
		if ( typeof trigger !== 'function' ) {
			glob.unregister(contexts,uid);
			return;
		}
		if ( typeof uid !== 'string' ) uid = glob.generateUid();
		if ( Array.isArray(contexts) ) contexts.forEach( c => {
			try {
				if ( typeof glob.prop.registered[c] === 'undefined') glob.prop.registered[c] = {};
				glob.prop.registered[c][uid] = trigger;
			} catch(e) {
				console.log("glob.register", e, contexts, uid, trigger );
			}
		});
		return ()=>{ glob.unregister(contexts, uid); };
	};

	/**
	 * @function
	 * @param { string|[string]} contexts The name(s) ot the status change variable(s)
	 * @param {string} uid
	 * @return { void }
	 * @description Unregister a function handle on a status change
	 */
	glob.unregister = ( contexts, uid ) => {
		if ( typeof uid !== 'string' ) return;
		if ( typeof contexts === 'string' ) contexts = [contexts];
		if ( Array.isArray(contexts) ) contexts.forEach( c => {
			try {
				if ( typeof glob.prop.registered[c] === 'object') delete glob.prop.registered[c][uid];
			} catch(e) {
				console.log("glob.unregister", e, uid, contexts );
			}
		});
	};

	/**
	 * @function
	 * @param {string} uid
	 * @description De-register this UID from whatever it was registered
	 */
	glob.deregister = ( uid ) => {
		if (typeof uid !== 'string') return;
		Object.keys( glob.prop.registered || {} ).forEach( k => {
			delete glob.prop.registered[k][uid];
		});
	};

	/**
	 * @function
	 * @param { string } context The name ot the status change variable
	 * @param {any} args
	 * @return { Promise<void> }
	 * @description Trigger a status variable change to all registered handlers
	 */
	glob.trigger = async ( context, ...args ) => {
		if ( typeof context !== 'string' ) return;
		if ( glob.prop.registered[context] ) Object.entries(glob.prop.registered[context]).forEach(([uid,trigger])=>{
			if ( typeof trigger === 'function' ) setTimeout( ()=>{ trigger.apply(undefined, args); }, 10);
		});
	};

	/**
	 * @function
	 * @param {string} s The status variable name
	 * @return {*}
	 * @description Get current value of a status variable
	 */
	glob.getStatus = ( s ) => { return glob.prop.status[s]; };

	/**
	 * @function
	 * @param {string} s The status variable name
	 * @param { any } v The value to be set
	 * @param { boolean } [persist] Data should persist
	 * @return {void}
	 * @description Set a status variable
	 */
	glob.setStatus = ( s, v, persist ) => {
		glob.prop.status[s] = v;
		if ( persist ) glob.setStorage( s, v );
		glob.trigger( s, v  );
	};

	/**
	 * @function
	 * @param {string} s The status variable name
	 * @param {boolean} [broadcast] Broadcast the change with trigger (default: false)
	 * @return { void }
	 * @description Remove a status variable and all its registered broadcasts
	 */
	glob.dropStatus = ( s, broadcast ) => {
		if ( typeof s === "string" ) s = [s];
		if ( ! Array.isArray(s) ) return;
		s.forEach( x =>{
			delete glob.prop.status[x];
			if ( broadcast ) glob.trigger(x);
			delete glob.prop.registered[x];
		})
	};

	/**
	 * @function
	 * @return {?string}
	 * @description Display name of user
	 */
	glob.getUserLoginName = () => {
		return glob.getStatus('userLoginName') || "";
	};

	/**
	 * @function
	 * @return {?string}
	 * @description Login name of user
	 */
	glob.getUserLogin = () => {
		return glob.getStatus('userLogin')?.user || "";
	};

	/**
	 * @function
	 * @return {string[]}
	 */
	glob.getUserLoginGroups = () => {
		return glob.getStatus('userLoginGroups') || [];
	};

	/**
	 * @function
	 * @return {boolean}
	 */
	glob.userIsAdmin = () => {
		return !! glob.getStatus('userLogin')?.isAdmin;
	}

	/**
	 * @function
	 * @param {object} userData
	 * @return {void}
	 */
	glob.setUserLogin = ( userData ) => {
		glob.setStatus('userLoginName', userData?.name || '');
		glob.setStatus('userLoginGroups', userData?.groups || [] );
		glob.setStatus('userLogin', userData ?? false );
	}

	/**
	 * @function
	 * @param { string } auth
	 * @return {Promise<Object>}
	 */
	glob.login = ( auth ) => {
		if ( typeof auth === 'undefined' ) auth = window.atob(window.sessionStorage.getItem('domino-items-auth'));
		return new Promise( ( resolve, reject ) => {
			fetch(
				glob.prop.uri.api + 'login',
				{
					cache: 'no-cache',
					headers: {
						Authorization: 'HB ' + auth,
					}
				}
			).then( response => {
				if ( response.ok ) {
					return response.json();
				} else {
					glob.setUserLogin();
					window.sessionStorage.removeItem('domino-items-auth');
					reject(new Error(String(response.status)+' Autenticazione fallita'));
				}
			}).then( data => {
				glob.setUserLogin(data);
				window.sessionStorage.setItem('domino-items-auth', window.btoa(auth) );
				resolve(data);
			}).catch( e => {
				glob.setUserLogin();
				window.sessionStorage.removeItem('domino-items-auth');
				reject(e);
			});
		})
	}

	if (  window.sessionStorage.getItem('domino-items-auth') ) {
		glob.login(  window.atob(window.sessionStorage.getItem('domino-items-auth')) );
	}

	/**
	 * @function
	 * @param {boolean} [force]
	 * @return { void }
	 */
	glob.logout = ( force ) => {
		window.sessionStorage.removeItem('domino-items-auth');
		glob.setUserLogin();
	}

	// -----------------------------------------------------------------------------------------------------------------

	/**
	 * @function
	 * @param { string } path
	 * @param { Object } [data]
	 * @param { string[] } [headers]
	 * @return { Promise<Object> }
	 * @description Performs an API request
	 */
	glob.api = ( path, data = {}, headers = [] ) => {
		/** @type { string } */
		const uri = glob.prop.uri.api + path;
		/** @type { Request } */
		const req = {
			cache: 'no-cache',
			method: 'POST',
			body: JSON.stringify( data ),
			headers: {
				Authorization: 'HB ' + window.atob(window.sessionStorage.getItem('domino-items-auth')),
				'Content-Type': 'application/json; charset=UTF-8',
				accept: 'application/json; charset=UTF-8, application/xml; charset=UTF-8',
				pragma: 'no-cache',
				'cache-control': 'no-cache'
			}
		};
		if ( headers.length ) {
			headers.forEach( h => { req.headers[h] = headers[h]; })
		}
		return new Promise( ( resolve, reject ) => {
			fetch(
				uri, req
			).then( response => {
				if ( response.ok ) return response.json();
				reject (new Error(String(response.status))+' '+response.statusText);
			}).then( data => {
				resolve( data );
			}).catch( e => {
				reject(e);
			})
		});
	}

	glob.getItemsList = () => {
		return new Promise((resolve, reject) => {
			if ( glob.prop.data.itemsList )
				return resolve(JSON.parse(JSON.stringify(glob.prop.data.itemsList)));
			window.fetch(
				glob.prop.uri.items,
				{
					method: 'GET',
					cache: 'no-cache',
					credentials: "include",
					headers: {
						'pragma': 'no-cache',
						'cache-control': 'no-cache',
						'Accept': 'application/json, text/json',
					},
					referrerPolicy: 'no-referrer',
				}
			).then( response => {
				if ( ! response.ok ) throw response;
				if ( glob.prop.isDeploy) {
					const u = response.headers.get('X-Auth-User');
					const g = response.headers.get('X-Auth-Group');
					if ( u ) glob.setUserLogin(u,g);
				} else {
					glob.setUserLogin('developer','Tesseract');
				}
				return response.json();
			}).then( jdata => {
				glob.prop.data.itemsList = jdata;
				resolve(JSON.parse(JSON.stringify(glob.prop.data.itemsList)));
			}).catch( e => {
				reject( e );
			});
		});
	};
	glob.getItemsGroupObject = () => {
		return new Promise( (resolve, reject)=>{
			if ( glob.prop.data.itemsGroupObject )
				return resolve(JSON.parse(JSON.stringify(glob.prop.data.itemsGroupObject)));
			glob.getItemsList().then( list => {
				const groups = {};
				list.forEach( i => {
					const settore = i.settore;
					const id = i.id ?? i.coditem;
					const name = i.name ?? i.item;
					const ateco = ( i.codateco || "").replace(/ +/g,"").split(",").join(", ");
					if ( typeof groups[settore] === 'undefined' ) groups[settore] = {};
					groups[settore][id] = {
						value: id,
						label: `${name}${ ateco ? " ["+ateco+"]" : ""}`
					};
				});
				glob.prop.data.itemsGroupObject = groups;
				resolve(JSON.parse(JSON.stringify(glob.prop.data.itemsGroupObject)));
			}).catch( e=>{
				reject(e);
			});
		});
	};
	glob.getItemsGroupList = () => {
		return new Promise( (resolve, reject)=>{
			if ( glob.prop.data.itemsGroupList )
				return resolve(JSON.parse(JSON.stringify(glob.prop.data.itemsGroupList)));
			glob.getItemsGroupObject().then( group => {
				const gs = [];
				Object.keys(group).sort().forEach( gk => {
					const its = [];
					const g = group[gk];
					Object.keys(g).sort( (a,b)=>{
						return ( g[a].label < g[b].label ? -1 : 1 );
					}).forEach( ik => {
						its.push( g[ik] );
					});
					gs.push({ label: gk, items: its });
				});
				glob.prop.data.itemsGroupList = gs;
				resolve(JSON.parse(JSON.stringify(glob.prop.data.itemsGroupList)));
			}).catch( e => {
				reject(e);
			});
		});
	};
	glob.getItemsGroups = ( exclude ) => {
		if ( typeof exclude === "undefined" ) exclude = [];
		else if ( typeof exclude === "string" ) exclude = [exclude];
		return new Promise( (resolve, reject) => {
			glob.getItemsGroupList().then( list =>{
				const out= [];
				list.forEach( g => {
					const its = [];
					g.items.forEach( i => {
						if ( ! exclude.includes(i.value) ) its.push(i);
					});
					if ( its.length ) out.push({ label: g.label, options: its });
				});
				resolve(JSON.parse(JSON.stringify(out)));
			}).catch(e => {
				reject(e);
			});
		});
	};
	glob.getAtecoList = () => {
		return new Promise((resolve, reject) => {
			if ( glob.prop.data.atecoList )
				return resolve(JSON.parse(JSON.stringify(glob.prop.data.atecoList)));
			window.fetch(
				glob.prop.uri.ateco,
				{
					method: 'GET',
					cache: 'no-cache',
					credentials: "include",
					headers: {
						'pragma': 'no-cache',
						'cache-control': 'no-cache',
						'Accept': 'application/json, text/json',
					},
					referrerPolicy: 'no-referrer',
				}
			).then( response => {
				if ( ! response.ok ) throw response;
				return response.json();
			}).then( jdata => {
				glob.prop.data.atecoList = jdata;
				resolve(JSON.parse(JSON.stringify(glob.prop.data.atecoList)));
			}).catch( e => {
				reject( e );
			});
		});
	};

	glob.getFormDefinition = ( itemid = glob.getStatus('currentItem').value ) => {
		return new Promise((resolve, reject) => {
			if ( typeof glob.prop.data.definitions !== 'object' ) glob.prop.data.definitions = {};
			if ( glob.prop.data.definitions[itemid] )
				return resolve(JSON.parse(JSON.stringify(glob.prop.data.definitions[itemid])));
			window.fetch(
				glob.prop.uri.formdef.replace(/#item#/g,itemid),
				{
					method: 'GET',
					cache: 'no-cache',
					credentials: "include",
					referrerPolicy: 'no-referrer',
					headers: {
						'pragma': 'no-cache',
						'cache-control': 'no-cache',
						'Accept': 'application/json, text/json',
					}
				}
			).then( response => {
				if ( ! response.ok ) throw response;
				return response.json();
			}).then( jdata => {
				glob.prop.data.definitions[itemid] = jdata;
				resolve(JSON.parse(JSON.stringify(glob.prop.data.definitions[itemid])));
			}).catch( e => {
				reject( e );
			});
		});
	};
	// -----------------------------------------------------------------------------------------------------------------

	glob.getFormData = ( itemid = glob.getStatus('currentItem').value ) => {
		return new Promise( (resolve, reject)=> {
			const uri = glob.prop.uri.getform;
			if ( glob.prop.isDeploy ) {
				window.fetch(
					uri.replace(/#item#/g,itemid),
					{
						method: 'GET',
						cache: 'no-cache',
						credentials: "include",
						referrerPolicy: 'no-referrer',
						headers: {
							'pragma': 'no-cache',
							'cache-control': 'no-cache',
							'Accept': 'application/json, text/json',
							"x-username": glob.getUserLoginGroup(),
							"x-form-type": itemid
						}
					}
				).then( response =>{
					if ( response.status === 404 ) return resolve({});
					if ( ! response.ok ) throw response;
					return response.json();
				}).then( jdata => {
					resolve(jdata);
				}).catch( e =>{
					reject(e);
				})
			} else {
				window.fetch(
					uri.replace(/#item#/g,itemid)+'?_='+glob.generateUid(),
					{
						method: 'GET',
						cache: 'no-cache',
						referrerPolicy: 'no-referrer',
						headers: {
							'Authorization' : 'Basic YTph',
							'pragma': 'no-cache',
							'cache-control': 'no-cache',
							'Accept': 'application/json, text/json',
							"x-username": 'Tesseract',
							"x-form-type": itemid
						}
					}
				).then( response =>{
					if ( response.status === 404 ) return resolve({});
					if ( ! response.ok ) throw response;
					return response.json();
				}).then( jdata => {
					resolve(jdata);
				}).catch( e =>{
					console.log("Error:",e);
					return resolve({});
					//reject(e);
				})
			}
		});
	};

	glob.saveFormData = ( fd ) => {
		const nfd = JSON.parse(JSON.stringify(fd));
		nfd.form_version = '0.2';
		return new Promise( (resolve, reject) => {
			const uri = glob.prop.uri.saveform;
			if ( glob.prop.isDeploy ) {
				const d = document.getElementById("documentMainDialog");
				if ( d ) {
					d.querySelector('.dialogTitle').innerText = "Accesso al server";
					d.querySelector('.dialogBody').innerText = "Salvataggio in corso…";
					d.style.display = 'block';
				}
				window.fetch(
					uri.replace(/#item#/g,nfd.item.value),
					{
						method: "POST",
						cache: 'no-cache',
						credentials: "include",
						referrerPolicy: 'no-referrer',
						headers: {
							"Content-Type": "application/json",
							'Accept': 'application/json',
							"x-username": glob.getUserLoginGroup(),
							"x-form-type": nfd.item.value
						},
						body: JSON.stringify(nfd),
					}).then( (response) =>{
					if ( d ) d.style.display = 'none';
					resolve(response.json());
				}).catch( e => {
					if ( d ) d.style.display = 'none';
					reject([e]);
				});
			} else {
				const d = document.getElementById("documentMainDialog");
				if ( d ) {
					d.querySelector('.dialogTitle').innerText = "Accesso al server";
					d.querySelector('.dialogBody').innerText = "Salvataggio in corso…";
					d.style.display = 'block';
				}
				window.fetch(
					uri.replace(/#item#/g,nfd.item.value),
					{
						method: "POST",
						cache: 'no-cache',
						referrerPolicy: 'no-referrer',
						headers: {
							'Authorization' : 'Basic YTph',
							"Content-Type": "application/json",
							'Accept': 'application/json',
							"x-username": 'Tesseract',
							"x-form-type": nfd.item.value
						},
						body: JSON.stringify(nfd),
					}).then( (response) =>{
					if ( d ) d.style.display = 'none';
					resolve(response.json());
				}).catch( e => {
					if ( d ) d.style.display = 'none';
					reject([e]);
				});
			}
		});
	};

	/**
	 * @function
	 * @param { Object } data
	 * @param { string } [filename]
	 * @return {void}
	 * @description Save on device a JSON file from data
	 */
	glob.saveJSON = (data, filename="output.json") => {
		if(!data) return;
		if(typeof data === "object") data = JSON.stringify(data, undefined, "\t");
		const blob = new Blob([data], {type: 'application/json'});
		const a = document.createElement('a');
		a.download = filename;
		a.href = window.URL.createObjectURL(blob);
		a.dataset.downloadurl =  ['application/json', a.download, a.href].join(':');
		a.click();
		setTimeout( ()=>{ a.remove() }, 1000 );
	}

	/**
	 * @function
	 * @param {string} [accept]
	 * @return {Promise<Object>}
	 * @description Return object parsed from JSON file
	 */
	glob.uploadJSON = ( accept = ".json" ) => {
		if ( typeof accept === "string" ) accept = [accept];
		accept = accept.map( x => x.replace(/^\*?\.?/,'')).map( x => '.'+x );
		window.document.body.querySelectorAll(':scope > input.autoInputField').forEach( x => {
			x.remove();
		})
		const input = window.document.createElement('input');
		input.setAttribute('type', 'file');
		input.setAttribute('accept', accept);
		input.style.display = 'none';
		input.className = 'autoInputField';
		window.document.body.appendChild(input);
		return new Promise( ( resolve ) => {
			input.addEventListener('change', () => {
				const selectedFile = input.files[0];
				if ( selectedFile ) {
					const reader = new window.FileReader();
					reader.addEventListener( 'load', () => {
						try {
							resolve( JSON.parse(reader.result) );
						} catch(e) {
							console.error(e);
							resolve( reader.result );
						}
					});
					reader.readAsText(selectedFile);
				} else {
					resolve(false );
				}
				input.remove();
			});
			setTimeout( ()=>{ input.click(); } );
		})
	}

	// -----------------------------------------------------------------------------------------------------------------

	//***** Navigation ********

	/** @typedef { import("./Glob").GlobPage } */
	/** @typedef { import("./Glob").GlobPageParams } */

	/**
	 * @function
	 * @param {boolean} [force]
	 * @return { GlobPage|string }
	 * @description Retrieve home page internal representation, keeping current page params
	 */
	glob.getHomePage = (force) => {
		let op = glob.prop.homePage;
		if (window.location.hash.startsWith("#!/")) {
			const parts = window.location.hash.split("/");
			parts.shift();
			if (parts[0]) {
				op = {'name': parts.shift(), 'params': {}};
				if (parts.length) parts.filter(i => i.includes("=")).forEach(kv => {
					const p = kv.split("=");
					if (p[0]) {
						const k = p.shift();
						const v = p.join("=");
						op.params[k] = v || "";
					}
				});
			}
		}
		return op;
	};

	/**
	 * @private
	 * @function
	 * @param { GlobPageParams } [obj]
	 * @return {string}
	 */
	glob.object2paramsHash = obj => {
		const parts = [];
		if ( obj && typeof obj === "object") Object.keys(obj)
			.filter(k => !!k)
			.filter( k => (typeof obj[k] === 'string' || typeof obj[k] === 'number') )
			.filter(k => ! k.startsWith('_'))
			.sort( (a,b) => a.toLowerCase().localeCompare(b.toLowerCase()) )
			.forEach(k => {
				let v = obj[k];
				v ??= "";
				parts.push(`${k}=${v}`);
			});
		return parts.length ? '/'+parts.join("/") : '';
	};

	/**
	 * @function
	 * @param {GlobPage|string} p [The page object, defaults to current page]
	 * @return {string}
	 */
	glob.page2uri = ( p = glob.getCurrentPage() ) => {
		if (!p) return;
		if (typeof p === "string") p = {"name": p, params:{} };
		return "#!/" + p.name + glob.object2paramsHash(p.params);
	};

	/**
	 * @function
	 * @param {GlobPage|string} op
	 * @param {boolean} [force]
	 * @return {void}
	 * @description Goto some page, with or without params, optionally with status change(s)
	 */
	glob.gotoPage = (op = glob.prop.homePage, force) => {
		if (!op) return;
		if (typeof op === "string") op = {"name": op,params:{} };
		let ns;
		if ( op.status) {
			ns = glob.clone( op.status);
		}
		const prevPage = glob.clone( glob.getCurrentPage() );
		if ( prevPage ) glob.setStatus('previousPage',prevPage);
		const newhash = "#!/" + op.name + glob.object2paramsHash(op.params);
		if (newhash !== window.location.hash) {
			window.history.pushState(null, op.name, glob.prop.uri.localpath + newhash);
			glob.setStatus('currentPage', op);
		} else if (force) {
			glob.setStatus('currentPage', op);
		}
		if ( ns ) {
			Object.entries(ns).forEach( ([k,v]) => {
				glob.setStatus(k,v);
			});
		}
	};

	/**
	 * @function
	 * @return {void}
	 * @description Back to home page
	 */
	glob.gotoHomePage = () => { glob.gotoPage() };

	/**
	 * @function
	 * @param {boolean} [force]
	 * @description Automatically detect page by current URI location string
	 */
	glob.gotoPageByLocation = (force) => {
		glob.gotoPage(glob.getHomePage(), force);
	};

	setTimeout(glob.gotoPageByLocation, 0);

	window.addEventListener('popstate', () => {
		glob.gotoPageByLocation(true);
	});

	/**
	 * @function
	 * @return {GlobPage|string}
	 * @description
	 */
	glob.getCurrentPage = () => {
		let mo = glob.getStatus('currentPage');
		if (mo) return mo;
		return glob.getHomePage();
	};

	/**
	 * @function
	 * @param { GlobPage } [page] Defaults to current page
	 * @return { GlobPageParams }
	 * @description Given a page (or the current page) returns its params
	 */
	glob.getPageParams = (page = glob.getCurrentPage() ) => {
		return (page?.params || {});
	};

	/**
	 * @function
	 * @param { string } pname Parameter name
	 * @param { GlobPage } [page] Defaults to current page
	 * @return { any }
	 * @description Given a page (or the current page) returns a named param value
	 */
	glob.getPageParam = (pname, page = glob.getCurrentPage()) => {
		const mp = glob.getPageParams( page );
		return mp[pname];
	};

	/**
	 * @function
	 * @param { GlobPageParams } params
	 * @return { void }
	 * @description Set all the params of the current page
	 */
	glob.setCurrentPageParams = (params= {} ) => {
		const mo = glob.getCurrentPage();
		mo.params = params;
		glob.gotoPage(mo);
	};

	/**
	 * @function
	 * @param { string } pname Param name
	 * @param { * } [pvalue] Param value, leave undefined to delete.
	 * @return { void }
	 * @description Set the value of a param for current page
	 */
	glob.setCurrentPageParam = (pname, pvalue) => {
		const params = glob.getPageParams();
		if ( typeof pvalue === "undefined" ) {
			delete params[pname];
		} else {
			params[pname] = pvalue;
		}
		glob.setCurrentPageParams(params);
	};

	/**
	 * @function
	 * @param { string } pname Param name
	 * @return { void }
	 * @description Remove a param for current page
	 */
	glob.rmCurrentPageParam = (pname) => { glob.setCurrentPageParam(pname) };

	// glob.closeAllDialogs = () => {
	// 	document.querySelectorAll('body > .dialogCurtain, body > .dialogBox').forEach(n => {
	// 		n.remove()
	// 	});
	// };

	/* ------ User prefs -----*/

	/**
	 * @function
	 * @param {string} key
	 * @return {*}
	 * @description Read a preference key
	 * @see setPref()
	 */
	glob.getPref = (key) => {
		const p = glob.getStatus('Prefs') || {};
		return p[key];
	};

	/**
	 * @function
	 * @param {string} key
	 * @param {*} value
	 * @return Set a Prefs key value, deletes it if value is undefined.
	 * @see getPref()
	 */
	glob.setPref = ( key, value ) => {
		const p = glob.getStatus('Prefs') || {};
		if ( typeof value === "undefined" ) {
			delete p[key];
		} else {
			p[key] = value;
		}
		glob.setStatus('Prefs', p, true);
		return value;
	};

	/* ------ Language ------*/

	/**
	 * @function
	 * @return {string} 2-characters, lowercase
	 * @description Current language
	 * @see setLanguage()
	 * @see getDefaultLanguage()
	 * @see getLanguageLong()
	 */
	glob.getLanguage = () => (glob.getPref('language') || glob.getDefaultLanguage());

	/**
	 * @function
	 * @param {string} [lang] Defaults to current language
	 * @return {string} Language in format “xx_XX”
	 * @description Long format of language
	 * @see getLanguage()
	 * @see setLanguage()
	 * @see getDefaultLanguage()
	 */
	glob.getLanguageLong = ( lang ) => {
		if ( lang && lang.length === 5 ) return lang;
		if ( typeof lang === 'undefined' ) {
			const ll = glob.getPref('languageLong');
			if ( ll ) return ll;
			lang = glob.getLanguage();
		}
		let longlang;
		switch ( lang ) {
			case 'en': longlang = "en-US"; break;
			default : longlang = lang + '-' + lang.toUpperCase()
		}
		return longlang;
	}

	/**
	 * @function
	 * @return {string} 2-characters, lowercase
	 * @see getLanguage()
	 * @see setLanguage()
	 * @see getLanguageLong()
	 */
	glob.getDefaultLanguage = () => ( glob.prop.labelsDB?.default );

	/**
	 * @function
	 * @param {string} [lang] 2-characters, lowercase
	 * @param {string} [langLong] Language in format “xx_XX”, optional
	 * @return {string} 2-characters, lowercase
	 * @description Set current language, or determines it automatically if parameter undefined
	 * @see getLanguage()
	 * @see getDefaultLanguage()
	 * @see getLanguageLong()
	 */
	glob.setLanguage = ( lang, langLong ) => {
		if ( ! glob.prop.labelsDB ) return setTimeout( ()=>{ glob.setLanguage(lang)} );
		const curlanguage = glob.getPref('language');
		if ( typeof lang === "undefined" && curlanguage ) lang = curlanguage;
		if ( typeof lang === "undefined" ) {
			const navl = glob.getStorage("Prefs")?.language;
			if ( navl ) lang = navl;
		}
		if ( typeof lang === "undefined") {
			const ll = window.navigator.language;
			const navl = ll.substring(0,2).toLowerCase();
			if ( glob.prop.labelsDB[navl] ) {
				lang = navl;
				langLong = ll;
			}
		}
		if ( typeof lang === "undefined") {
			(window.navigator.languages||[]).forEach( cl => {
				if ( typeof lang === "string" ) return;
				const navl = cl.substring(0,2).toLowerCase();
				if ( glob.prop.labelsDB[navl] ) {
					lang = navl;
					langLong = cl;
				}
			});
		}
		if ( lang && ! glob.prop.labelsDB[lang] ) lang = undefined;
		if ( typeof lang === "undefined") {
			const ulangs = (window.navigator.languages||[])
				.filter(x => (x && typeof x === "string") )
				.map( x => x.substring(0,2).toLowerCase() );
			const applangs = Object.keys(glob.prop.labelsDB)
				.filter( x => (x.length === 2) );
			lang = ulangs.find( x => ( applangs.includes(x) ) )||glob.prop.labelsDB.default;
		}
		if ( lang !== curlanguage ) {
			if ( ! langLong || langLong.length !== 5 ) langLong = glob.getLanguageLong( lang  );
			window.document.documentElement.setAttribute('lang',langLong);
			const p = glob.getStorage('Prefs') || {};
			p.language = lang;
			p.languageLong = langLong;
			glob.setStatus('Prefs', p, true);
		}
		return lang;
	};

	/**
	 * @function
	 * @param {string} key Label key
	 * @param {Object} [values] Optional parameters
	 * @param {string} [lang] Language, defaults to current language
	 * @return {string}
	 * @description Converts a key in a localized label
	 */
	glob.label = ( key, values, lang ) => {
		if (typeof key !== "string" ) return "";
		if ( typeof lang === "undefined" ) lang = glob.getLanguage();
		if ( typeof lang === "undefined" ) return "";
		if ( ! (glob.prop.labelsDB && glob.prop.labelsDB[lang]) ) return "";
		let txt = glob.prop.labelsDB[lang][key] ?? glob.prop.labelsDB[glob.prop.labelsDB.default][key];
		if ( typeof txt === "undefined" ) return key;
		if ( values && typeof values === "object" ) {
			Object.entries( values ).forEach( ([k,v])=> {
				const re = new RegExp('\\{\\{'+k+'(\\|[^}]*)?\\}\\}','g');
				txt = txt.replace( re, v );
			});
		}
		txt = txt.replace(/\{\{[^|}]+\|([^}]*)\}\}/g,"$1");
		return txt;
	};

	/**
	 * @function
	 * @param {string} key Icon key
	 * @return {string}
	 * @description Labels in “icon” language
	 */
	glob.icon = ( key ) => glob.label( key, undefined, 'icon' );


	// -------- Storage ------

	/**
	 * @function
	 * @return {Object}
	 * @description Returns the storage the app is currently using
	 */
	glob.getStorageRepo = () => {
		if ( glob.prop.applicationStorage ) return glob.prop.applicationStorage;
		if ( ! (window.localStorage || window.sessionStorage )) return glob.prop.applicationStorage = {};
		if ( typeof glob.prop.storageCanPersist === 'undefined') {
			const ls = window.localStorage.getItem(glob.prop.keyApplicationName) ?? undefined;
			if (typeof ls !== 'undefined' ) glob.prop.storageCanPersist = true;
		}
		if ( window.localStorage && glob.prop.storageCanPersist ) {
			const ls = window.localStorage.getItem(glob.prop.keyApplicationName) ?? undefined;
			if (typeof ls !== 'undefined') return glob.prop.applicationStorage = JSON.parse(ls);
			window.localStorage.setItem(glob.prop.keyApplicationName,'{}');
		} else if ( window.sessionStorage ) {
			const ls = window.sessionStorage.getItem(glob.prop.keyApplicationName) ?? undefined;
			if (typeof ls !== 'undefined') return glob.prop.applicationStorage = JSON.parse(ls);
			window.sessionStorage.setItem(glob.prop.keyApplicationName,'{}');
		}
		return glob.prop.applicationStorage = {};
	};

	/**
	 * @function
	 * @param {string} item Key name of the property
	 * @return {*}
	 */
	glob.getStorage = ( item ) => {
		const s = glob.getStorageRepo();
		return s[item];
	};

	/**
	 * @function
	 * @param {boolean} persist
	 * @description Sets the use of localStorage (persist) or sessionStorage (don't persist)
	 */
	glob.setStoragePersistence = ( persist = false ) => {
		if ( ! (window.localStorage && window.sessionStorage )) return;
		const cs = JSON.stringify(glob.getStorageRepo());
		if ( persist ) {
			window.localStorage.setItem( glob.prop.keyApplicationName, cs );
			window.sessionStorage.removeItem(glob.prop.keyApplicationName);
		} else {
			window.sessionStorage.setItem( glob.prop.keyApplicationName, cs );
			window.localStorage.removeItem(glob.prop.keyApplicationName);
		}
		glob.prop.storageCanPersist = !! persist;
	};

	/**
	 * @function
	 * @param {string} item Key name of the property
	 * @param {any} [value]
	 * @return {void}
	 * @description Sets the value of a storage key, deletes it if value is undefined
	 */
	glob.setStorage = ( item, value ) => {
		if ( ! (window.localStorage || window.sessionStorage )) return;
		const s = glob.getStorageRepo();
		if ( typeof value === 'undefined') {
			delete s[item];
		} else {
			s[item] = value;
		}
		glob.prop.applicationStorage = s;
		if ( window.localStorage && glob.prop.storageCanPersist ) {
			window.localStorage.setItem(glob.prop.keyApplicationName, JSON.stringify(s));
		} else  if ( window.sessionStorage ) {
			window.sessionStorage.setItem(glob.prop.keyApplicationName, JSON.stringify(s));
		}
	}

	/**
	 * @function
	 * @return {void}
	 * @description Resets and completely remove any track of application storage
	 */
	glob.zapStorage = () => {
		if ( ! (window.localStorage || window.sessionStorage )) return;
		if ( window.localStorage && glob.prop.storageCanPersist ) {
			window.localStorage.removeItem(glob.prop.keyApplicationName);
		} else  if ( window.sessionStorage ) {
			window.sessionStorage.removeItem(glob.prop.keyApplicationName);
		}
	}

	/* ------ */

	(()=>{
		if ( window.matchMedia('(display-mode: standalone)').matches ) document.body.classList.add('standalone');
		if ( ! glob.prop.loadedComponents ) glob.prop.loadedComponents = { labels: true };
		glob.setLanguage();
		Object.entries( (glob.getStorageRepo()||{}) ).forEach( ([k,v]) => {
			glob.setStatus(k,v);
		});
		glob.prop.loadedComponents.core = true;
	})()

	window.glob = glob;

}

/* Author: Marco Balestra <balestra@altersoftware.it> - Y-2022-2025 */
